import { pullAllBy } from "lodash";
import { observable } from "mobx";
import { flow, types, Instance } from "mobx-state-tree";
import { intl } from "../../i18n/TypedIntl";
import { logger } from "../../utils";
import { validateFileExtension, validateFileSize } from "../../utils/filesValidator";
import {
  UploadFailedTooBig,
  UploadFailedUnsupportedType,
  UploadFailedVirusFound,
  UploadFailedMalformedFileContent
} from "../../utils/fileUploadResponses";
import { IAttachment } from "../chat/chatItems";
import {
  FileUploadInfo,
  FileUploadState,
  IFileUploadInfo,
  uploadInfoFromIntentAttachment
} from "../fileUpload";
import {
  sharedChatNotificationGetter,
  sharedModelsGetter,
  sharedServicesGetters
} from "../shared/storeUtils";

export const FileUploadStore = types
  .model("FileUploadStore")
  .views(sharedServicesGetters)
  .views(sharedChatNotificationGetter)
  .views(sharedModelsGetter)
  .extend((self) => {
    const uploads = observable.array<IFileUploadInfo>();
    const intentAttachmentsArray = observable.array<IAttachment>();

    const actions = {
      createFileUpload(file: File, roomId: string, startTransfer = true): IFileUploadInfo {
        const existingFileUpload = uploads.find((el) => isFilesEqual(el.file, file));
        if (!existingFileUpload) {
          const fileUpload = new FileUploadInfo(file, roomId);
          uploads.push(fileUpload);
          if (startTransfer) {
            actions.startFileUpload(fileUpload);
          }
          return fileUpload;
        }
        return existingFileUpload;
      },

      startFileUpload(upload: IFileUploadInfo) {
        if (upload.uploadState !== FileUploadState.IN_PROGRESS) {
          actions.uploadFile(upload);
        }
      },

      removeFileUpload(upload: IFileUploadInfo) {
        const uploadedFile = uploads.find((f) => f.id === upload.id);

        if (uploadedFile) {
          // self.sharedSpinnerService
          //   .deleteFile(uploadedFile)
          //   .then(() => {
          //     pullAllBy(uploads, [uploadedFile], "id");
          //   })
          //   .catch((err) => {
          //     logger.error({ message: "Couldn't delete file", error: err });
          //   });
          pullAllBy(uploads, [uploadedFile], "id");
        } else {
          pullAllBy(intentAttachmentsArray, [upload], "id");
        }
      },

      setIntentAttachments(attachments: IAttachment[]) {
        intentAttachmentsArray.replace(attachments);
      },

      clear() {
        uploads.clear();
        intentAttachmentsArray.clear();
      },

      uploadFile: flow(function* (fileUpload: IFileUploadInfo) {
        const handleFileVirusFound = () => {
          self.sharedChatNotificationStore.setChatNotification({
            message: intl.formatMessage("widget.addFiles.error.virusFound")
          });
          fileUpload.uploadState = FileUploadState.VIRUS_FOUND;
        };
        const handleFileTooBig = () => {
          self.sharedChatNotificationStore.setChatNotification({
            message: intl.formatMessage("widget.addFiles.error.tooBig")
          });
          fileUpload.uploadState = FileUploadState.TOO_BIG;
        };
        const handleFileUnsupported = () => {
          self.sharedChatNotificationStore.setChatNotification({
            message: intl.formatMessage("widget.addFiles.error.unsupportedType")
          });
          fileUpload.uploadState = FileUploadState.UNSUPPORTED_TYPE;
        };

        const handleMalformedFileContent = () => {
          self.sharedChatNotificationStore.setChatNotification({
            message: intl.formatMessage("widget.addFiles.error.malformedFileContent")
          });
          fileUpload.uploadState = FileUploadState.MALFORMED_FILE_CONTENT;
        };

        try {
          fileUpload.uploadState = FileUploadState.IN_PROGRESS;
          const maximumAttachmentSizeBytes = self.sharedOrgConfig?.maximumAttachmentSizeBytes;
          const allowedAttachmentExtensions = self.sharedOrgConfig?.allowedAttachmentExtensions;

          if (!maximumAttachmentSizeBytes) {
            logger.error("maximumAttachmentSizeBytes is required and missing");
            handleFileTooBig();
            return;
          }

          if (!allowedAttachmentExtensions) {
            logger.error("allowedAttachmentExtensions is required and missing");
            handleFileUnsupported();
            return;
          }

          if (!validateFileSize(fileUpload.file, maximumAttachmentSizeBytes)) {
            handleFileTooBig();
            return;
          }

          if (!validateFileExtension(fileUpload.file, allowedAttachmentExtensions)) {
            handleFileUnsupported();
            return;
          }

          fileUpload.attachment = yield self.sharedSpinnerService.uploadFile(fileUpload.file, fileUpload.roomId);
          fileUpload.uploadState = FileUploadState.SUCCEEDED;
        } catch (e) {
          logger.error({ message: "Upload failed", error: e });
          if (e instanceof UploadFailedVirusFound) {
            handleFileVirusFound();
          } else if (e instanceof UploadFailedTooBig) {
            handleFileTooBig();
          } else if (e instanceof UploadFailedUnsupportedType) {
            handleFileUnsupported();
          } else if (e instanceof UploadFailedMalformedFileContent) {
            handleMalformedFileContent();
          } else {
            fileUpload.uploadState = FileUploadState.FAILED;
          }
        }
      })
    };

    const views = {
      get fileUploads(): ReadonlyArray<IFileUploadInfo> {
        try {
          const intentUploads = views.intentAttachments.slice().map(uploadInfoFromIntentAttachment);
          return [...uploads.slice(), ...intentUploads];
        } catch (e) {
          return uploads.slice();
        }
      },

      get attachments(): ReadonlyArray<IAttachment> {
        return [...views.uploadAttachments, ...views.intentAttachments];
      },

      get uploadAttachments(): ReadonlyArray<IAttachment> {
        return uploads.filter((el) => !!el.attachment).map((el) => el.attachment!);
      },

      get intentAttachments(): ReadonlyArray<IAttachment> {
        return intentAttachmentsArray;
      },

      get isUploadCompleted() {
        return uploads.every((upload) => upload.isCompleted);
      },

      get hasFiles() {
        return !!uploads.length || !!intentAttachmentsArray.length;
      }
    };

    return { actions, views };
  });

const isFilesEqual = (file1: File, file2: File) => {
  return file1.name === file2.name && file1.size === file2.size;
};

export interface IFileUploadStore extends Instance<typeof FileUploadStore> { }
